import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TfiTrash } from "react-icons/tfi";
import { Disable } from 'react-disable';

import { connect } from "react-redux";
import { compose } from "redux";


import { getFinding, addAttachmentAction, editAction, getActionDetails, addActionComment, addFindingComment } from "../../store/actions/riskActions";
import { getUsers } from "../../store/actions/adminActions";
import Select, { components } from "react-select";

import moment from 'moment';
import debounce from "lodash/debounce";
import { ChevronDown, ChevronRight } from "lucide-react";
import { CommentsSection } from "../Comments";
import { SidePanel, ModalHeader, ModalBody, RaboInput, RaboTextarea, RaboSelect } from "../ui";




function FindingField({ label, children, className }) {
    return (
        <div className={className}>
            <label className="block text-[12px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                {label}
            </label>
            {children}
        </div>
    );
}

const FindingOverlay = (props) => {

    const {finding_comments, comments, addFindingCommentFN, finding, finding_id, getFindingFN, closeFinding, onSave, profile, open = true, onClose } = props;
    const { t } = useTranslation();
    const [commentsOpen, setCommentsOpen] = useState(false);




    const [form, setForm] = useState({
        id: "",                 // derived from finding_id + year
        title: "",
        priority: "Medium",     // not in DB; pick a sensible default
        status: "Open",         // maps from DB (Open | Closed | Problem)
        finding_date: "",       // yyyy-mm-dd
        due_date: "",           // not in DB yet
        assigned_to: "",        // not in DB yet
        category: "",           // not in DB yet
        description: "",
        root_cause: "",
        impact: "",
        recommendation: "",
        control_title: "",
        approval_status: "",

    });

    const badgeColor = {
        High: "bg-amber-100 text-amber-800 border-amber-300",
        Medium: "bg-yellow-100 text-yellow-800 border-yellow-300",
        Low: "bg-green-100 text-green-800 border-green-300",
        Urgent: "bg-red-100 text-red-800 border-red-300",
    }[form.priority] || "bg-gray-100 text-gray-700 border-gray-300";

    const statusColor = {
        "In Progress": "bg-purple-100 text-purple-800 border-purple-300",
        Open: "bg-blue-100 text-blue-800 border-blue-300",
        Closed: "bg-green-100 text-green-800 border-green-300",
        Problem: "bg-rose-100 text-rose-800 border-rose-300",
    }[form.status] || "bg-gray-100 text-gray-700 border-gray-300";


    useEffect(() => {
        if (finding_id)
            getFindingFN(finding_id);

    }, [finding_id]);


    useEffect(() => {
        if (!finding) return;

        const year = finding.finding_date
            ? new Date(finding.finding_date).getFullYear()
            : new Date().getFullYear();

        setForm({
            id: `FIN-${year}-${String(finding.id).padStart(3, "0")}`,
            title: finding.title ?? "",
            priority: "Medium", // still filler until you add this to DB
            status: finding.status || "Open", // 'Open' | 'Closed' | 'Problem'
            finding_date: toDateInput(finding.finding_date), // -> 'YYYY-MM-DD'
            due_date: "",           // filler
            assigned_to: finding.owner_name || "",        // filler
            category: "",           // filler
            description: finding.description ?? "",
            root_cause: finding.root_cause ?? "",
            impact: finding.impact ?? "",
            recommendation: finding.recommendation ?? "",
            control_title: finding.control_title ?? "",
            approval_status: finding.approval_status ?? "",
        });
    }, [finding]);


    const toDateInput = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const handleAddComment = async (comment, finding) => {
        await addFindingCommentFN(comment, finding);
        getFindingFN(finding_id);
    };

    const handleClose = () => {
        closeFinding(form, finding_id);
        onClose?.();
    };

    if (!open) {
        return null;
    }

    return (
        <SidePanel
            onClose={handleClose}
            overlayZIndex="z-[400]"
            resizable
            defaultWidth={Math.round(window.innerWidth * 0.6)}
            minWidth={420}
            className="rounded-tl-2xl"
        >
            <ModalHeader onClose={handleClose}>
                <div className="min-w-0 pr-6">
                    <RaboInput
                        className="border-0 bg-transparent shadow-none focus:border-0 focus:bg-transparent h-auto py-0 px-0 text-[15px] font-semibold text-gray-900"
                        value={form.title}
                        placeholder={t("findingsPage.detailModal.titlePlaceholder")}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                    <p className="text-[12px] text-gray-400 mt-0.5">
                        {t("findingsPage.detailModal.findingIdLabel")}: {form.id}
                    </p>
                </div>
            </ModalHeader>

            <ModalBody className="space-y-4 flex-1 overflow-y-auto py-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FindingField label={t("findingsPage.detailModal.findingDate")}>
                        <RaboInput
                            type="date"
                            value={form.finding_date}
                            onChange={(e) => setForm({ ...form, finding_date: e.target.value })}
                        />
                    </FindingField>

                    <FindingField label={t("findingsPage.detailModal.owner")}>
                        <RaboInput
                            readOnly
                            value={form.assigned_to || form.owner_name || ""}
                        />
                    </FindingField>

                    <FindingField label={t("findingsPage.detailModal.status")}>
                        <RaboSelect
                            value={form.status || "Open"}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                        >
                            <option value="Open">{t("findingsPage.detailModal.statusOpen")}</option>
                            <option value="Problem">{t("findingsPage.detailModal.statusProblem")}</option>
                            <option value="Closed">{t("findingsPage.detailModal.statusClosed")}</option>
                        </RaboSelect>
                    </FindingField>

                    <FindingField label={t("findingsPage.detailModal.approval")}>
                        <RaboSelect
                            value={form.approval_status || "Pending"}
                            onChange={(e) => setForm({ ...form, approval_status: e.target.value })}
                        >
                            <option value="Pending">{t("findingsPage.detailModal.approvalPending")}</option>
                            <option value="Approved">{t("findingsPage.detailModal.approvalApproved")}</option>
                            <option value="Rejected">{t("findingsPage.detailModal.approvalRejected")}</option>
                        </RaboSelect>
                    </FindingField>
                </div>

                <FindingField label={t("findingsPage.detailModal.control")}>
                    <RaboInput readOnly value={form.control_title || ""} />
                </FindingField>

                <FindingField label={t("findingsPage.detailModal.description")}>
                    <RaboTextarea
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                </FindingField>

                <FindingField label={t("findingsPage.detailModal.rootCause")}>
                    <RaboTextarea
                        rows={3}
                        value={form.root_cause}
                        onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                    />
                </FindingField>

                <FindingField label={t("findingsPage.detailModal.impact")}>
                    <RaboTextarea
                        rows={3}
                        value={form.impact}
                        onChange={(e) => setForm({ ...form, impact: e.target.value })}
                    />
                </FindingField>

                <FindingField label={t("findingsPage.detailModal.recommendation")}>
                    <RaboTextarea
                        rows={3}
                        value={form.recommendation}
                        onChange={(e) => setForm({ ...form, recommendation: e.target.value })}
                    />
                </FindingField>

                <div>
                    <button
                        type="button"
                        onClick={() => setCommentsOpen((o) => !o)}
                        className="flex items-center gap-1.5 text-[12px] font-medium text-gray-500 uppercase tracking-wide mb-3"
                    >
                        {commentsOpen ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                        )}
                        {t("findingsPage.detailModal.comments")}
                        {Array.isArray(comments) && comments.length > 0 && (
                            <span className="text-gray-400 normal-case">({comments.length})</span>
                        )}
                    </button>

                    {commentsOpen && (
                        <CommentsSection
                            comments={comments}
                            profile={profile}
                            showTitle={false}
                            placeholder={t("findingsPage.detailModal.commentsPlaceholder")}
                            addButtonLabel={t("findingsPage.detailModal.addComment")}
                            unknownUserLabel={t("findingsPage.detailModal.unknownUser")}
                            itemClassName="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-gray-600 mb-3"
                            onSubmit={(html) => handleAddComment(html, finding_id)}
                        />
                    )}
                </div>
            </ModalBody>
        </SidePanel>
    );

};


const mapStateToProps = (state) => {
    //console.log("Finding ", state.risk.control_details)
    return {

        finding: state.risk.control_details?.finding_details,
        comments: state.risk.control_details?.finding_comments,
        profile: state.profile.profile,

    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        addAttachmentActionFN: (risk_id, action_id, file) => dispatch(addAttachmentAction(risk_id, action_id, file)),
        editActionFN: (risk_id, action_id, action) => dispatch(editAction(risk_id, action_id, action)),
        getActionDetailsFN: (risk_id, action_id) => dispatch(getActionDetails(risk_id, action_id)),
        addActionCommentFN: (comment, action_id) => dispatch(addActionComment(comment, action_id)),
        getFindingFN: (finding_id) => dispatch(getFinding(finding_id)),
        getUsersFN: () => dispatch(getUsers()),
        addFindingCommentFN:(comment, finding) => dispatch(addFindingComment(comment, finding))

    };
};


export default compose(connect(mapStateToProps, mapDispatchToProps))(FindingOverlay);